import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Inbox, Loader2, Mail, MoreVertical, XCircle, Eye } from 'lucide-react';
import { ExpressionOfInterestListItem, EOIStatus, TierFeatureKey } from '@court-sync/shared';
import { eoiService } from '../../../../services/eoi';
import { Button, Card, CardContent } from '../../../../components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { PaginatedList } from '@/components/ui/PaginatedList';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';

const EOI_PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: EOIStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'invited', label: 'Invited' },
  { value: 'declined', label: 'Declined' },
  { value: 'booked', label: 'Booked' },
];

function DetailBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

function formatOptionalText(value: string | undefined | null): string {
  const t = (value ?? '').trim();
  return t.length > 0 ? t : '—';
}

function EoiDetailsDialog({
  row,
  open,
  onOpenChange,
}: {
  row: ExpressionOfInterestListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!row) return null;

  const isGuest =
    !!(row.guestContactEmail || row.guestGuardianFirstName || row.guestGuardianLastName);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Expression of interest</DialogTitle>
          <DialogDescription>
            {row.programName ? `${row.programName} · ` : ''}
            {row.lessonName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <DetailBlock label="Player">
            <p>
              {row.studentFirstName} {row.studentLastName}
            </p>
          </DetailBlock>

          <DetailBlock label="Parent / guardian">
            {isGuest ? (
              <div className="space-y-1">
                <p>
                  {[row.guestGuardianFirstName, row.guestGuardianLastName].filter(Boolean).join(' ').trim() || '—'}
                </p>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Email:</span>{' '}
                  {formatOptionalText(row.guestContactEmail)}
                </p>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Phone:</span>{' '}
                  {formatOptionalText(row.guestPhone)}
                </p>
              </div>
            ) : row.customerId ? (
              <div className="space-y-1">
                <p>
                  {[row.customerFirstName, row.customerLastName].filter(Boolean).join(' ').trim() || '—'}
                </p>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">Email:</span>{' '}
                  {formatOptionalText(row.customerEmail)}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Details will link after the family books with an invitation.</p>
            )}
          </DetailBlock>

          <DetailBlock label="Experience level">
            <p className="capitalize">{row.experienceLevel}</p>
          </DetailBlock>

          <DetailBlock label="Goals">{formatOptionalText(row.goals)}</DetailBlock>

          <DetailBlock label="Availability">{formatOptionalText(row.availability)}</DetailBlock>

          <DetailBlock label="Message">{formatOptionalText(row.message)}</DetailBlock>

          <DetailBlock label="Status">
            <p className="capitalize">{row.status}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Submitted {new Date(row.createdAt).toLocaleString()}
            </p>
          </DetailBlock>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export type ExpressionsOfInterestPanelProps = {
  academyId: string;
  termIdFilter: string;
  lessonIdFilter: string;
};

export function ExpressionsOfInterestPanel({
  academyId,
  termIdFilter,
  lessonIdFilter,
}: ExpressionsOfInterestPanelProps) {
  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const [items, setItems] = useState<ExpressionOfInterestListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<EOIStatus | ''>('');
  const [actionId, setActionId] = useState<string | null>(null);
  const [detailRow, setDetailRow] = useState<ExpressionOfInterestListItem | null>(null);

  useEffect(() => {
    setPage(1);
  }, [termIdFilter, lessonIdFilter]);

  const load = useCallback(async () => {
    if (!academyId || !hasProgramsAccess) return;
    setLoading(true);
    try {
      const listRes = await eoiService.listEOIs(
        academyId,
        {
          status: statusFilter || undefined,
          lessonId: lessonIdFilter !== 'all' ? lessonIdFilter : undefined,
          termId:
            lessonIdFilter === 'all' && termIdFilter !== 'all' ? termIdFilter : undefined,
        },
        page,
        EOI_PAGE_SIZE
      );
      if (listRes.success && listRes.data !== undefined) {
        setItems(listRes.data);
        setPagination(listRes.pagination ?? null);
      } else toast.error(listRes.error || 'Failed to load');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [academyId, hasProgramsAccess, statusFilter, termIdFilter, lessonIdFilter, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async (id: string) => {
    setActionId(id);
    try {
      await eoiService.sendInvitation(id, academyId);
      toast.success('Invitation sent');
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to send invitation');
    } finally {
      setActionId(null);
    }
  };

  const decline = async (id: string) => {
    const notes = window.prompt('Optional notes (internal):') ?? undefined;
    setActionId(id);
    try {
      await eoiService.declineEOI(id, academyId, notes);
      toast.success('Updated');
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    } finally {
      setActionId(null);
    }
  };

  return (
    <>
      <EoiDetailsDialog row={detailRow} open={detailRow !== null} onOpenChange={(o) => !o && setDetailRow(null)} />

      <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          Status
          <select
            className="input ml-2"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as EOIStatus | '');
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading && pagination === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" aria-hidden />
        </div>
      ) : pagination && pagination.total === 0 ? (
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={Inbox} />
          {statusFilter ? (
            <>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No submissions match this filter
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
                Try another status or clear the filter to see all expressions of interest.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setStatusFilter('');
                  setPage(1);
                }}
              >
                Clear filter
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No expressions of interest yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                When families submit interest for an invitation-only lesson, they appear here. Use the
                booking scope above to filter by term or lesson.
              </p>
            </>
          )}
        </DashboardEmptyStateCard>
      ) : (
        <PaginatedList
          data={items}
          pagination={pagination}
          loading={loading && pagination !== null}
          onPageChange={setPage}
          emptyMessage="No expressions of interest on this page."
          renderItem={(row) => {
            const canInviteOrDecline = row.status === 'pending' || row.status === 'reviewed';
            const busy = actionId === row.id;
            return (
              <Card key={row.id}>
                <CardContent className="p-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {row.studentFirstName} {row.studentLastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {row.lessonName}
                      {row.programName ? ` · ${row.programName}` : ''} · {row.experienceLevel}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(row.createdAt).toLocaleString()} ·{' '}
                      <span className="capitalize">{row.status}</span>
                    </p>
                  </div>
                  <div className="flex justify-end sm:justify-start">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          disabled={busy}
                          className="h-8 w-8 shrink-0 border-border/80 bg-background shadow-sm hover:bg-muted/60"
                          aria-label="Expression of interest actions"
                        >
                          <MoreVertical className="h-4 w-4" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" sideOffset={4} className="w-56">
                        <DropdownMenuItem
                          onSelect={() => {
                            setDetailRow(row);
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" aria-hidden />
                          View details
                        </DropdownMenuItem>
                        {canInviteOrDecline && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem disabled={busy} onSelect={() => void invite(row.id)}>
                              <Mail className="mr-2 h-4 w-4" aria-hidden />
                              Send invitation
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={busy}
                              className="text-destructive focus:text-destructive"
                              onSelect={() => void decline(row.id)}
                            >
                              <XCircle className="mr-2 h-4 w-4" aria-hidden />
                              Decline
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          }}
        />
      )}
    </>
  );
}
